/**
 * When an abandoned session expires, as arithmetic.
 *
 * The policy is separated from the component that runs the clock for the same
 * reason `@mgc/domain` is separated from the screens: this is the part that can
 * be wrong in a way nobody sees until a customer pays for somebody else's
 * basket, and a `setInterval` inside a React tree is not a thing anybody can
 * write a test against. Everything here is a pure function of "how long has it
 * been quiet, and what is the kiosk doing" — no timers, no DOM, no store.
 */
import type { Step } from '@/store/kiosk'

/**
 * How long a step tolerates silence before it starts counting down.
 *
 * Published kiosk guidance converges on 30–90 seconds for a customer-facing
 * idle period, so 60 is the middle of the range everyone agrees on. Payment is
 * given twice as long deliberately: the customer is getting a card out of a
 * wallet or a phone out of a pocket, which is a genuine pause rather than an
 * abandonment, and clearing a basket somebody is about to pay for is the one
 * false positive this feature can produce that actually costs a sale.
 *
 * `welcome` and `success` are absent for opposite reasons — one is already the
 * attract screen, and the other owns a shorter timer of its own.
 */
export const IDLE_MS: Partial<Record<Step, number>> = {
  'order-type': 60_000,
  menu: 60_000,
  checkout: 60_000,
  payment: 120_000
}

/**
 * How long the countdown runs once it starts.
 *
 * A silent expiry is its own defect, so this is the window in which a customer
 * who is simply reading slowly can say so.
 */
export const WARN_MS = 20_000

export type IdleVerdict =
  /** Nothing to do. Either the step has no timer, or the customer is active. */
  | { readonly kind: 'quiet' }
  /** Show the countdown. */
  | { readonly kind: 'warn'; readonly remainingMs: number }
  /** Clear the session. */
  | { readonly kind: 'expire' }

/**
 * What the kiosk should do about the silence so far.
 *
 * `paying` outranks everything. It is true only while a charge is actually
 * outstanding — a terminal authorising, a QR settling — and while it is true
 * there is no verdict to reach: a card in a terminal is money on the counter,
 * and a session boundary that can interrupt one is worse than no boundary at
 * all. It is deliberately *not* the update gate's `paymentInFlight`, which the
 * payment screen holds for its whole lifetime; a customer who reaches that
 * screen and wanders off is still an abandoned basket.
 */
export function idleVerdict(input: {
  readonly step: Step
  readonly paying: boolean
  readonly quietForMs: number
}): IdleVerdict {
  if (input.paying) return { kind: 'quiet' }

  const idleAfter = IDLE_MS[input.step]
  if (idleAfter === undefined) return { kind: 'quiet' }

  const remainingMs = idleAfter + WARN_MS - input.quietForMs
  if (remainingMs <= 0) return { kind: 'expire' }
  if (input.quietForMs >= idleAfter) return { kind: 'warn', remainingMs }
  return { kind: 'quiet' }
}
